import { useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useChatAttachment } from "@/components/live-room/LiveChatAttach";

// Admin-only: post a flyer (image) / announcement to the board at the top of the Feed.
export default function FlyerComposer({ open, onOpenChange, onCreated }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [expires, setExpires] = useState("");
  const [publishAt, setPublishAt] = useState("");
  const [pinned, setPinned] = useState(false);
  const [busy, setBusy] = useState(false);
  const attach = useChatAttachment();
  const att = attach.att;

  const reset = () => { setTitle(""); setBody(""); setLink(""); setExpires(""); setPublishAt(""); setPinned(false); attach.clear(); };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Give the flyer a title."); return; }
    if (!att?.url && !body.trim()) { toast.error("Add a flyer image or some text."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/announcements", {
        title: title.trim(), body: body.trim() || null, image_url: att?.url || null,
        link_url: link.trim() || null, pinned,
        expires_at: expires ? new Date(expires).toISOString() : null,
        publish_at: publishAt ? new Date(publishAt).toISOString() : null,
      });
      toast.success(publishAt ? `Scheduled for ${new Date(publishAt).toLocaleString()}.` : "Flyer posted.");
      onCreated?.(data); reset(); onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't post the flyer.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-md" data-testid="flyer-composer">
        <DialogHeader><DialogTitle className="font-display text-xl">Post a flyer</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Title (e.g. Friday Night Battle Royale)" data-testid="flyer-title-input" className="bg-black/40 border-white/10" />
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={600} rows={3} placeholder="Details (optional)" data-testid="flyer-body-input" className="bg-black/40 border-white/10" />
          <input ref={attach.fileRef} type="file" accept="image/*,.heic,.heif" className="hidden" onChange={attach.pick} data-testid="flyer-image-input" />
          {att ? (
            <div className="relative border border-white/10 bg-black/40 overflow-hidden" data-testid="flyer-image-preview">
              <img src={att.localUrl} alt="" className="w-full max-h-56 object-contain bg-black" />
              {att.status === "uploading" && <div className="absolute inset-x-0 bottom-0 h-1 bg-white/20"><div className="h-full bg-rose-500" style={{ width: `${att.progress}%` }} /></div>}
              <div className="absolute top-2 left-2 text-[11px] px-2 py-0.5 bg-black/70 text-white" data-testid="flyer-image-status">
                {att.status === "uploading" ? `Uploading ${att.progress}%` : att.status === "ready" ? "Ready" : "Upload failed"}
              </div>
              {att.status === "error" && <button type="button" onClick={attach.retry} className="absolute bottom-2 left-2 text-[11px] px-2 py-0.5 bg-amber-400 text-black" data-testid="flyer-image-retry">Retry</button>}
              <button type="button" onClick={attach.clear} aria-label="Remove image" data-testid="flyer-image-remove" className="absolute top-2 right-2 h-7 w-7 grid place-items-center bg-black/70 text-white"><X size={14} /></button>
            </div>
          ) : (
            <button type="button" onClick={attach.open} data-testid="flyer-image-pick"
              className="w-full h-24 border border-dashed border-white/20 bg-black/30 hover:bg-white/5 text-zinc-400 flex items-center justify-center gap-2 text-sm transition-colors">
              <ImagePlus size={16} /> Add flyer image
            </button>
          )}
          <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Link (optional, https://…)" data-testid="flyer-link-input" className="bg-black/40 border-white/10" />
          <label className="text-xs text-zinc-400 flex flex-col gap-1">Publish at (optional — hidden from members until then)
            <input type="datetime-local" value={publishAt} onChange={(e) => setPublishAt(e.target.value)} data-testid="flyer-publish-at-input" className="bg-black/40 border border-white/10 text-white text-sm px-2 py-1.5 [color-scheme:dark]" />
          </label>
          <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
            <label className="text-xs text-zinc-400 flex flex-col gap-1">Expires (optional)
              <input type="datetime-local" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="flyer-expires-input" className="bg-black/40 border border-white/10 text-white text-sm px-2 py-1.5 [color-scheme:dark]" />
            </label>
            <label className="text-xs text-zinc-300 flex items-center gap-2 pt-4 cursor-pointer">
              <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} data-testid="flyer-pinned-input" className="accent-amber-400" /> Pin to top
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="flyer-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={busy || att?.status === "uploading"} data-testid="flyer-submit-btn" className="bg-amber-400 text-black hover:bg-amber-300">
              {busy && <Loader2 size={14} className="animate-spin mr-1" />} {publishAt ? "Schedule flyer" : "Post flyer"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
